import { 
  collection, doc, onSnapshot, query, where, orderBy, getDocs, limit, updateDoc, getDoc, setDoc, documentId, deleteDoc, addDoc 
} from "firebase/firestore";
import { db } from "../firebase";
import { LiveOrder } from "../types";
import { getTenantId } from "./tenantService";

export const sendLiveOrder = async (order: LiveOrder) => {
    // Already handled in CartView via new checkout API.
    // This function can be kept for backward compatibility and internal usages without payment/validations 
    // or we can remove it entirely. Keeping it for now for consistency if used elsewhere.
    const tenantId = getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    const sanitizedOrder = JSON.parse(JSON.stringify(order));
    
    // Recursive function to strip out huge base64 strings (starting with "data:") to prevent Firestore 1MB limits
    const recursivelySanitizeImages = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(recursivelySanitizeImages);
        const clean: any = {};
        for (const key of Object.keys(obj)) {
            const val = obj[key];
            if (key === 'imageUrl' && typeof val === 'string' && val.startsWith('data:')) {
                clean[key] = '';
            } else if (typeof val === 'string' && val.startsWith('data:') && val.length > 1000) {
                clean[key] = '';
            } else {
                clean[key] = recursivelySanitizeImages(val);
            }
        }
        return clean;
    };
    
    const fullySanitizedOrder = recursivelySanitizeImages(sanitizedOrder);
    
    await setDoc(doc(ordersCol, order.id), fullySanitizedOrder, { merge: true });
};

export const subscribeToPendingOrders = (callback: (orders: LiveOrder[]) => void) => {
    const tenantId = getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    const q = query(
        ordersCol, 
        where("status", "in", ["pending", "accepted", "ready", "shipped"])
    );
    
    return onSnapshot(q, (snapshot) => {
        const orders: LiveOrder[] = [];
        snapshot.forEach(d => {
            orders.push({ ...(d.data() as any), id: d.id } as LiveOrder);
        });
        callback(orders);
    });
};

export const getOrderTime = (o: any) => {
    let t1 = 0, t2 = 0;
    if (o.timestamp) {
        if (typeof o.timestamp === 'number') t1 = o.timestamp;
        else if (o.timestamp.toMillis) t1 = o.timestamp.toMillis();
        else if (o.timestamp.seconds) t1 = o.timestamp.seconds * 1000;
        else t1 = new Date(o.timestamp).getTime();
    }
    if (o.createdAt) {
        if (typeof o.createdAt === 'number') t2 = o.createdAt;
        else if (o.createdAt.toMillis) t2 = o.createdAt.toMillis();
        else if (o.createdAt.seconds) t2 = o.createdAt.seconds * 1000;
        else t2 = new Date(o.createdAt).getTime();
    }
    return Math.max(t1, t2) || 0;
};

export const subscribeToOrderHistory = (limitCount: number, callback: (orders: LiveOrder[]) => void) => {
    const tenantId = getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    
    const q = query(
        ordersCol, 
        orderBy("timestamp", "desc"), 
        limit(limitCount)
    );

    return onSnapshot(q, (snapshot) => {
        const orders = snapshot.docs.map(d => ({ ...(d.data() as any), id: d.id } as LiveOrder));
        callback(orders);
    });
};

export const getOrdersBetweenDates = async (startTimestamp: number, endTimestamp: number): Promise<LiveOrder[]> => {
    const tenantId = getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    
    const q = query(
        ordersCol, 
        where("timestamp", ">=", startTimestamp),
        where("timestamp", "<=", endTimestamp),
        orderBy("timestamp", "desc")
    );
    
    const sn = await getDocs(q);
    const orders = sn.docs.map(d => ({ ...d.data() as any, id: d.id } as LiveOrder));

    return orders;
};

export const subscribeToOrdersSince = (timestamp: number, callback: (orders: LiveOrder[]) => void) => {
    const tenantId = getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    
    const q = query(
        ordersCol, 
        where("timestamp", ">=", timestamp),
        orderBy("timestamp", "desc")
    );
    
    return onSnapshot(q, (snapshot) => {
        const orders = snapshot.docs.map(d => ({ ...d.data() as any, id: d.id } as LiveOrder));
        callback(orders);
    });
};

export const syncCustomerOnOrderCompletion = async (order: LiveOrder) => {
    if (!order.customerPhone) return;
    const cleanPhone = order.customerPhone.replace(/\D/g, '');
    if (cleanPhone.length < 5) return;
    
    // Guard to ensure points and stats are never credited more than once per order
    if (order.loyaltyPointsCredited) {
        return;
    }

    try {
        const tenantId = getTenantId() || 'root';
        const customersCol = collection(db, tenantId === 'root' ? 'customers' : `tenants/${tenantId}/customers`);
        
        // Find existing customer by phone
        const q = query(customersCol, where("phone", "==", cleanPhone), limit(1));
        const snap = await getDocs(q);

        const earned = order.pointsEarned || 0;
        const redeemed = order.pointsRedeemed || 0;
        const orderTotal = order.total || 0;

        const cleanPayload = (obj: any) => JSON.parse(JSON.stringify(obj));

        if (!snap.empty) {
            // Customer exists! 
            // Since this order didn't have loyaltyPointsCredited = true, but the customer already exists,
            // we do NOT update/increment the stats again to prevent double-counting.
            // Their historic stats are already processed and accurate.
            console.log(`syncCustomerOnOrderCompletion: Customer with phone ${cleanPhone} already exists inside collection. Skipping duplicate update for order ${order.id}.`);
        } else {
            // Customer does NOT exist! Create a new one
            const initialBalance = order.loyaltyPointsSnapshot !== undefined 
                ? order.loyaltyPointsSnapshot 
                : Math.max(0, earned - redeemed);

            const initialData = cleanPayload({
                phone: cleanPhone,
                name: order.customerName || '',
                loyaltyPoints: initialBalance,
                lastOrderDate: Date.now(),
                totalOrders: 1,
                totalSpent: orderTotal,
                birthDate: order.customerBirthDate || undefined
            });

            await addDoc(customersCol, initialData);
        }

        // Set the lock flag on the order to prevent duplicate execution
        const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
        await updateDoc(doc(ordersCol, order.id), { loyaltyPointsCredited: true });
    } catch (e) {
        console.error("Error synchronizing customer on order completion:", e);
    }
};

export const resolveOrder = async (orderId: string, explicitTenantId?: string) => {
    const tenantId = explicitTenantId || getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    
    // Fetch order first to check customer presence and update loyalty
    try {
        const orderSnap = await getDoc(doc(ordersCol, orderId));
        if (orderSnap.exists()) {
            const orderData = { ...orderSnap.data(), id: orderSnap.id } as LiveOrder;
            await syncCustomerOnOrderCompletion(orderData);
        }
    } catch (err) {
        console.warn("Failed to fetch order for customer syncing inside resolveOrder:", err);
    }

    await updateDoc(doc(ordersCol, orderId), { 
        status: 'completed', 
        completedAt: Date.now() 
    });
};

export const reopenOrder = async (orderId: string, explicitTenantId?: string) => {
    const tenantId = explicitTenantId || getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    await updateDoc(doc(ordersCol, orderId), { status: 'pending' });
};

export const updateOrderShipment = async (orderId: string, trackingUrl: string, carrier: string, explicitTenantId?: string) => {
    const tenantId = explicitTenantId || getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    await updateDoc(doc(ordersCol, orderId), { trackingUrl, carrier });
};

export const markOrderAsShippedInternal = async (orderId: string, explicitTenantId?: string) => {
    const tenantId = explicitTenantId || getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    await updateDoc(doc(ordersCol, orderId), { status: 'shipped' });
};

export const deleteOrder = async (orderId: string) => {
    const tenantId = getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    
    // Real delete instead of soft cancel
    await deleteDoc(doc(ordersCol, orderId));
};

export const getOrderById = async (orderId: string): Promise<LiveOrder | null> => {
    const tenantId = getTenantId() || 'root';
    const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    const sn = await getDoc(doc(ordersCol, orderId));
    if (sn.exists()) {
        return { ...sn.data(), id: sn.id } as LiveOrder;
    }
    return null;
};

export const subscribeToOrderById = (orderId: string, callback: (order: LiveOrder | null) => void) => {
    let tenantId = getTenantId();
    if (!tenantId && typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        tenantId = urlParams.get('shop') || '';
    }
    if (!tenantId) tenantId = 'root';

    const targetCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
    
    let fallbackUnsub: (() => void) | null = null;

    const primaryUnsub = onSnapshot(doc(targetCol, orderId), (docSnap) => {
        if (docSnap.exists()) {
            callback({ ...(docSnap.data() as any), id: docSnap.id } as LiveOrder);
        } else if (tenantId !== 'root') {
            // Fallback: Check in root live_orders collection if not found in tenant subcollection
            if (!fallbackUnsub) {
                fallbackUnsub = onSnapshot(doc(collection(db, 'live_orders'), orderId), (rootSnap) => {
                    if (rootSnap.exists()) {
                        callback({ ...(rootSnap.data() as any), id: rootSnap.id } as LiveOrder);
                    } else {
                        callback(null);
                    }
                });
            }
        } else {
            callback(null);
        }
    });

    return () => {
        primaryUnsub();
        if (fallbackUnsub) fallbackUnsub();
    };
};

export const getCustomerHistory = async (phone: string): Promise<LiveOrder[]> => {
    if (!phone) return [];
    try {
        const tenantId = getTenantId() || 'root';
        const ordersCol = collection(db, tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`);
        const cleanPhone = phone.replace(/\D/g, '');
        
        let q = query(
            ordersCol,
            where("customerPhone", "==", cleanPhone),
            orderBy("timestamp", "desc"),
            limit(10)
        );
        
        let snapshot = await getDocs(q);
        
        if (snapshot.empty) {
            q = query(
                ordersCol,
                where("customerPhone", "==", phone),
                orderBy("timestamp", "desc"),
                limit(10)
            );
            snapshot = await getDocs(q);
        }

        const res = snapshot.docs.map(doc => ({ ...(doc.data() as any), id: doc.id } as LiveOrder));
        return res;
    } catch (e) {
        return [];
    }
};
